// MonCompteHero — refonte du profil utilisateur selon la maquette validée,
// branchée sur l'architecture AYA réelle : wallet (/v3/wallet/balance),
// commandes (/v3/dispatch/orders/mine), adresses PADA, billets, tabs existants.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  Search, Bell, Settings, Crown, BadgeCheck, QrCode, ChevronRight, MapPin,
  Building2, Wallet, Eye, EyeOff, ArrowUp, ArrowDown, ReceiptText, MoreHorizontal,
  ShoppingBag, Ticket, FileText, CalendarCheck, Heart, Map as MapIcon, Pencil,
  Share2, Home, Tag, Car, Package, CreditCard, Star, QrCode as Codes, Headphones,
  UserRound, ShieldCheck, Languages, Lock, SlidersHorizontal, CircleHelp, LogOut, X,
} from "lucide-react";
import AvatarUploader from "@/components/AvatarUploader";
import AddressShareCard from "@/components/AddressShareCard";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const T = {
  dark: { padding: "14px 14px 16px", borderRadius: 20, background: "linear-gradient(175deg, #06281A 0%, #04140C 100%)", border: "1px solid rgba(16,185,129,0.3)", marginBottom: 14 },
  iconBtn: { width: 36, height: 36, borderRadius: "50%", background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.35)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", position: "relative", color: "#CFE5D6" },
  tile: { flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 7, padding: "9px 9px", borderRadius: 12, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(16,185,129,0.28)", cursor: "pointer", color: "#ECF5EE", textAlign: "left" },
  tileT: { fontSize: 11.5, fontWeight: 800, lineHeight: 1.15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  tileS: { fontSize: 9, color: "#9DB4A6", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  secHead: { display: "flex", alignItems: "center", gap: 8, margin: "16px 2px 9px" },
  secIcon: { width: 24, height: 24, borderRadius: 8, background: "rgba(14,122,79,0.12)", display: "flex", alignItems: "center", justifyContent: "center" },
  secTitle: { fontSize: 15, fontWeight: 800, color: "#1F2937", margin: 0, flex: 1 },
  card: { background: "#fff", borderRadius: 14, border: "1px solid rgba(31,41,55,0.08)", boxShadow: "0 2px 10px rgba(31,41,55,0.05)" },
  row2: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 8 },
  srvRow: { display: "flex", alignItems: "center", gap: 9, padding: "11px 11px", cursor: "pointer", background: "#fff", borderRadius: 13, border: "1px solid rgba(31,41,55,0.08)", boxShadow: "0 2px 8px rgba(31,41,55,0.04)", textAlign: "left", width: "100%", minWidth: 0, boxSizing: "border-box" },
  srvIcon: (bg, c) => ({ width: 34, height: 34, borderRadius: 10, background: bg, color: c, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }),
  srvT: { fontSize: 12, fontWeight: 800, color: "#1F2937", lineHeight: 1.15 },
  srvS: { fontSize: 9.5, color: "#6B7280", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
};

const fmt = (n) => `${Number(n || 0).toLocaleString("fr-FR").replace(/\u202F|\u00A0/g, " ")} FCFA`;
const soon = (l) => toast.info(`${l} — bientôt disponible`);

export default function MonCompteHero({ user, isAuthenticated, home, others, onOpenTab, onEditHome, onLogout, onLogin, navigate }) {
  const [balance, setBalance] = useState(null);
  const [showBal, setShowBal] = useState(true);
  const [ordersCount, setOrdersCount] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendTo, setSendTo] = useState("");
  const [sendAmount, setSendAmount] = useState("");
  const [sending, setSending] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [transfers, setTransfers] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  const openHistory = async () => {
    setHistOpen(true);
    setTransfers(null);
    try {
      const r = await fetch(`${API}/v3/wallet/transfers`, { headers: { Authorization: `Bearer ${token}` } });
      const b = await r.json();
      setTransfers(b.ok ? b.transfers : []);
    } catch { setTransfers([]); }
  };

  const token = localStorage.getItem("civ_user_token") || "";
  // Guest JWT auto-provisionné ≠ vrai compte : wallet et QR réservés aux comptes réels.
  const isRealUser = isAuthenticated && !!user?.phone && !String(user.phone).startsWith("guest") && localStorage.getItem("civ_is_guest") !== "1";
  const displayName = (user && (user.name || user.display_name)) || "Invité AYA";
  const role = user?.role === "owner" ? "Owner" : user?.role === "commerce" ? "Commerçant" : "Membre";
  const commune = user?.commune || home?.commune || home?.commune_name || "—";
  const addrCount = (home ? 1 : 0) + (others?.length || 0);
  const namedCount = (others || []).filter((a) => a.name).length + (home?.name ? 1 : 0);

  useEffect(() => {
    if (!isRealUser || !token) return;
    const h = { Authorization: `Bearer ${token}` };
    fetch(`${API}/v3/wallet/balance`, { headers: h }).then((r) => r.json())
      .then((b) => { if (b.ok) setBalance(b.balance_fcfa); }).catch(() => {});
    fetch(`${API}/v3/dispatch/orders/mine?active=false&limit=100`, { headers: h }).then((r) => r.json())
      .then((b) => { if (b.ok) setOrdersCount(b.count ?? (b.orders || []).length); }).catch(() => {});
  }, [isRealUser, token]);

  const goTab = (id) => {
    onOpenTab(id);
    setTimeout(() => document.querySelector('[data-testid="mc-tabs"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  // 2026-06 — Partage premium : carte visuelle AYA (canvas + mini-carte) → WhatsApp.
  const shareHome = () => setShareOpen(true);

  const ACTIVITIES = [
    { id: "achats", Icon: ShoppingBag, bg: "#DCFCE7", c: "#0E7A4F", t: "Mes achats", s: ordersCount !== null ? `${ordersCount} commande${ordersCount > 1 ? "s" : ""}` : "Commandes", act: () => goTab("deliveries") },
    { id: "billets", Icon: Ticket, bg: "#FFEDD5", c: "#EA580C", t: "Mes billets", s: "Avion, Car, Taxi", act: () => navigate("/mes-billets") },
    { id: "factures", Icon: FileText, bg: "#DBEAFE", c: "#2563EB", t: "Mes reçus", s: "Reçus fiscaux", act: () => goTab("recus") },
    { id: "reservations", Icon: CalendarCheck, bg: "#EDE9FE", c: "#7C3AED", t: "Mes réservations", s: "Hôtels, Restaurants", act: () => soon("Réservations") },
    { id: "favoris", Icon: Heart, bg: "#FCE7F3", c: "#DB2777", t: "Mes favoris", s: "Produits, Lieux", act: () => goTab("favorites") },
  ];

  const SERVICES = [
    { Icon: Car, bg: "#FFEDD5", c: "#EA580C", t: "Mes transports", s: "Taxi, Car, Avion", act: () => navigate("/transport") },
    { Icon: Package, bg: "#FEF3C7", c: "#B45309", t: "Mes commandes", s: "Suivi & historique", act: () => goTab("deliveries") },
    { Icon: FileText, bg: "#DBEAFE", c: "#2563EB", t: "Mes reçus", s: "Documents fiscaux", act: () => goTab("recus") },
    { Icon: CalendarCheck, bg: "#EDE9FE", c: "#7C3AED", t: "Mes réservations", s: "Hôtels, Restaurants", act: () => soon("Réservations") },
    { Icon: CreditCard, bg: "#FFEDD5", c: "#EA580C", t: "Mon wallet", s: "Solde & transactions", act: () => document.getElementById("mc-wallet-card")?.scrollIntoView({ behavior: "smooth", block: "center" }) },
    { Icon: Star, bg: "#FEF9C3", c: "#A16207", t: "Mes abonnements", s: "Aya Pro, Services", act: () => navigate("/pro") },
    { Icon: Codes, bg: "#FEE2E2", c: "#DC2626", t: "Mes tickets & codes", s: "Billets, QR codes", act: () => navigate("/mes-billets") },
    { Icon: Headphones, bg: "#DCFCE7", c: "#0E7A4F", t: "Mon support", s: "Aide & assistance", act: () => navigate("/aya-prive?ctx=" + encodeURIComponent("Support AYA : j'ai besoin d'aide sur mon compte.")) },
  ];

  const SETTINGS = [
    { Icon: UserRound, bg: "#DBEAFE", c: "#2563EB", t: "Mon profil", s: "Infos personnelles", act: () => goTab("settings") },
    { Icon: ShieldCheck, bg: "#DCFCE7", c: "#0E7A4F", t: "Sécurité", s: "OTP, connexion", act: () => soon("Sécurité avancée") },
    { Icon: Bell, bg: "#FEE2E2", c: "#DC2626", t: "Notifications", s: "Email, SMS, App", act: () => soon("Réglages notifications") },
    { Icon: Languages, bg: "#DBEAFE", c: "#2563EB", t: "Langue", s: "Français, Nouchi…", act: () => soon("Choix de langue") },
    { Icon: Lock, bg: "#F3E8FF", c: "#7C3AED", t: "Confidentialité", s: "Données personnelles", act: () => soon("Confidentialité") },
    { Icon: SlidersHorizontal, bg: "#E0F2FE", c: "#0369A1", t: "Préférences", s: "Affichage, thème", act: () => goTab("settings") },
    { Icon: CircleHelp, bg: "#FEF3C7", c: "#B45309", t: "Aide", s: "Centre d'aide", act: () => navigate("/aya-prive") },
    { Icon: LogOut, bg: "#FEE2E2", c: "#DC2626", t: "Se déconnecter", s: "Quitter mon compte", act: onLogout },
  ];

  return (
    <div data-testid="mc-hero-mockup">
      {/* ══ BLOC SUPÉRIEUR DARK GREEN ══ */}
      <div style={T.dark}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <span style={{ fontSize: 16, fontWeight: 900, letterSpacing: 0.4, color: "#ECF5EE" }}>Mon compte</span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button aria-label="Rechercher" data-testid="mc-top-search" style={T.iconBtn} onClick={() => navigate("/")}> <Search size={16} /> </button>
            <button aria-label="Notifications" data-testid="mc-top-notif" style={T.iconBtn} onClick={() => soon("Notifications")}>
              <Bell size={16} />
              <span style={{ position: "absolute", top: 7, right: 8, width: 7, height: 7, borderRadius: "50%", background: "#EF4444" }} />
            </button>
            <button aria-label="Réglages" data-testid="mc-top-settings" style={T.iconBtn}
              onClick={() => document.getElementById("mc-settings-grid")?.scrollIntoView({ behavior: "smooth", block: "start" })}>
              <Settings size={16} />
            </button>
            {isRealUser ? (
              <button aria-label="Se déconnecter" data-testid="mc-logout-btn" onClick={onLogout}
                style={{ ...T.iconBtn, border: "1px solid rgba(239,68,68,0.45)", background: "rgba(239,68,68,0.10)", color: "#F87171" }}>
                <LogOut size={15} />
              </button>
            ) : (
              <button data-testid="mc-hero-login-btn" onClick={onLogin}
                style={{ display: "inline-flex", alignItems: "center", gap: 5, height: 34, padding: "0 14px", borderRadius: 999, border: "none", cursor: "pointer", background: "linear-gradient(135deg,#10B981,#0E7A4F)", color: "#fff", fontSize: 12, fontWeight: 800, boxShadow: "0 3px 10px rgba(16,185,129,0.35)" }}>
                Se connecter
              </button>
            )}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <AvatarUploader size={72} name={displayName} testid="mc-avatar" />
          <div style={{ flex: 1, minWidth: 130 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span data-testid="moncompte-name" style={{ fontSize: 20, fontWeight: 900, color: "#fff" }}>{displayName}</span>
              {isRealUser && <BadgeCheck size={17} color="#2FCF62" fill="rgba(47,207,98,0.2)" />}
            </div>
            <div style={{ fontSize: 11.5, color: "#9DB4A6", marginTop: 2 }}>
              {user?.created_at && isRealUser ? `Membre depuis ${new Date(user.created_at).getFullYear()}` : (isRealUser ? "Compte AYA vérifié" : "Mode invité")}
            </div>
            {(user?.role === "owner" || user?.role === "commerce") && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 6, padding: "3px 11px", borderRadius: 999, background: "rgba(212,175,55,0.14)", border: "1px solid rgba(212,175,55,0.55)", color: "#EFC75E", fontSize: 10.5, fontWeight: 800 }}>
                <Crown size={11} /> {role}
              </span>
            )}
          </div>
          <button data-testid="mc-qr-btn" onClick={() => (isRealUser ? setQrOpen(true) : toast.error("Connecte-toi pour ton QR AYA"))} style={{
            display: "flex", alignItems: "center", gap: 9, padding: "11px 11px", borderRadius: 13,
            background: "rgba(16,185,129,0.1)", border: "1.5px solid rgba(16,185,129,0.5)", cursor: "pointer", color: "#ECF5EE", textAlign: "left",
          }}>
            <QrCode size={24} color="#2FCF62" />
            <span>
              <span style={{ display: "block", fontSize: 12, fontWeight: 800 }}>Mon QR AYA</span>
              <span style={{ display: "block", fontSize: 9.5, color: "#9DB4A6" }}>Partager mon profil</span>
            </span>
            <ChevronRight size={14} color="#9DB4A6" />
          </button>
        </div>

        {/* 3 tuiles */}
        <div style={{ display: "flex", gap: 7, marginTop: 12 }}>
          <button style={T.tile} data-testid="mc-tile-addresses" onClick={() => goTab("addresses")}>
            <MapPin size={15} color="#2FCF62" style={{ flexShrink: 0 }} />
            <span style={{ minWidth: 0 }}><span style={T.tileT}>{addrCount} adresse{addrCount > 1 ? "s" : ""}</span><br /><span style={T.tileS}>Voir mes adresses</span></span>
          </button>
          <button style={T.tile} data-testid="mc-tile-commune" onClick={() => goTab("addresses")}>
            <Building2 size={15} color="#60A5FA" style={{ flexShrink: 0 }} />
            <span style={{ minWidth: 0 }}><span style={T.tileT}>{commune}</span><br /><span style={T.tileS}>Ma commune</span></span>
          </button>
          <button style={T.tile} data-testid="mc-tile-status" onClick={() => toast.info(`Statut du compte : ${role}`)}>
            <Crown size={15} color="#EFC75E" style={{ flexShrink: 0 }} />
            <span style={{ minWidth: 0 }}><span style={T.tileT}>{role}</span><br /><span style={T.tileS}>Mon statut</span></span>
          </button>
        </div>

        {/* Wallet */}
        <div id="mc-wallet-card" data-testid="mc-wallet-card" style={{
          marginTop: 12, padding: "13px 13px", borderRadius: 15,
          background: "linear-gradient(135deg, rgba(212,175,55,0.10), rgba(6,40,26,0.5))",
          border: "1.5px solid rgba(212,175,55,0.5)", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(212,175,55,0.16)", border: "1px solid rgba(212,175,55,0.45)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Wallet size={20} color="#EFC75E" />
          </div>
          <div style={{ flex: 1, minWidth: 140 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, fontWeight: 800, color: "#ECF5EE" }}>
              Mon Wallet
              <button aria-label="Afficher/masquer" data-testid="mc-wallet-eye" onClick={() => setShowBal((v) => !v)} style={{ background: "none", border: "none", cursor: "pointer", padding: 2, color: "#9DB4A6", display: "flex" }}>
                {showBal ? <Eye size={14} /> : <EyeOff size={14} />}
              </button>
            </div>
            <div data-testid="mc-wallet-balance" style={{ fontSize: 24, fontWeight: 900, color: "#fff", lineHeight: 1.1, marginTop: 2 }}>
              {!isRealUser ? "— —" : balance === null ? "…" : showBal ? fmt(balance) : "•••••• FCFA"}
            </div>
            <div style={{ fontSize: 9, color: "#9DB4A6", marginTop: 3 }}>Orange Money · MTN · Wave (sandbox)</div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            {[
              { Icon: ArrowUp, l: "Envoyer", tid: "send", act: () => (isRealUser ? setSendOpen(true) : toast.error("Connecte-toi pour envoyer de l'argent")) },
              { Icon: ArrowDown, l: "Recevoir", tid: "receive", act: () => (isRealUser ? setQrOpen(true) : soon("Réception")) },
              { Icon: ReceiptText, l: "Historique", tid: "history", act: () => (isRealUser ? openHistory() : toast.error("Connecte-toi pour voir l'historique")) },
              { Icon: MoreHorizontal, l: "Plus", tid: "more", act: () => soon("Options wallet") },
            ].map((a) => (
              <button key={a.tid} data-testid={`mc-wallet-${a.tid}`} onClick={a.act} style={{ background: "none", border: "none", cursor: "pointer", textAlign: "center", color: "#CFE5D6", padding: 0 }}>
                <span style={{ ...T.iconBtn, margin: "0 auto", width: 40, height: 40, display: "flex" }}><a.Icon size={16} color={a.tid === "send" ? "#2FCF62" : "#CFE5D6"} /></span>
                <span style={{ display: "block", fontSize: 9, fontWeight: 700, marginTop: 4 }}>{a.l}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ══ MES ACTIVITÉS (fond clair) ══ */}
      <div style={T.secHead}>
        <span style={T.secIcon}><ShoppingBag size={13} color="#0E7A4F" /></span>
        <h2 style={T.secTitle}>Mes activités</h2>
        <button data-testid="mc-activities-all" onClick={() => goTab("deliveries")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11.5, fontWeight: 700, color: "#0E7A4F", display: "flex", alignItems: "center", gap: 3 }}>
          Voir tout <ChevronRight size={13} />
        </button>
      </div>
      <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }} data-testid="mc-activities-row">
        {ACTIVITIES.map((a) => (
          <button key={a.id} data-testid={`mc-activity-${a.id}`} onClick={a.act} style={{ ...T.srvRow, flex: "0 0 auto", width: 168 }}>
            <span style={T.srvIcon(a.bg, a.c)}><a.Icon size={16} /></span>
            <span style={{ minWidth: 0, flex: 1 }}><span style={T.srvT}>{a.t}</span><br /><span style={T.srvS}>{a.s}</span></span>
            <ChevronRight size={13} color="#9CA3AF" />
          </button>
        ))}
      </div>

      {/* ══ MON ADRESSE PRINCIPALE ══ */}
      <div style={T.secHead}>
        <span style={T.secIcon}><MapPin size={13} color="#DC2626" /></span>
        <h2 style={T.secTitle}>Mon adresse principale</h2>
        <button data-testid="mc-home-map" onClick={() => navigate("/carte-publique")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11.5, fontWeight: 700, color: "#0E7A4F", display: "flex", alignItems: "center", gap: 3 }}>
          Voir sur la carte <ChevronRight size={13} />
        </button>
      </div>
      <div style={{ ...T.card, padding: 12 }} data-testid="mc-home-card">
        {home ? (
          <>
            <div style={{ display: "flex", gap: 6, marginBottom: 7 }}>
              <span style={{ padding: "3px 11px", borderRadius: 999, background: "#0E7A4F", color: "#fff", fontSize: 10.5, fontWeight: 800 }}>{home.name || "Maison"}</span>
              <span style={{ padding: "3px 11px", borderRadius: 999, background: "#fff", color: "#0E7A4F", fontSize: 10.5, fontWeight: 800, border: "1.5px solid #0E7A4F" }}>Principale</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1F2937" }}>{home.description || home.name || "Domicile"}</div>
            {home.commune && <div style={{ fontSize: 11.5, color: "#6B7280", marginTop: 2 }}>Commune de {home.commune}</div>}
            {home.address_civ && <code style={{ display: "block", fontSize: 10.5, color: "#0E7A4F", fontWeight: 800, marginTop: 5, letterSpacing: 0.5 }}>{home.address_civ}</code>}
            <div style={{ display: "flex", gap: 7, marginTop: 11 }}>
              {[
                { Icon: MapIcon, l: "Carte", tid: "map", act: () => navigate(`/navigate?dest=${home.lat},${home.lng}&code=${encodeURIComponent(home.address_civ || "")}&label=${encodeURIComponent(home.name || "Domicile")}`) },
                { Icon: Pencil, l: "Modifier", tid: "edit", act: onEditHome },
                { Icon: Share2, l: "Partager", tid: "share", act: shareHome },
                { Icon: MoreHorizontal, l: "Plus", tid: "plus", act: () => goTab("addresses") },
              ].map((b) => (
                <button key={b.tid} data-testid={`mc-home-action-${b.tid}`} onClick={b.act} style={{ flex: 1, padding: "8px 4px", borderRadius: 11, background: "#F7F5EE", border: "1px solid rgba(31,41,55,0.09)", cursor: "pointer", textAlign: "center", color: "#374151" }}>
                  <b.Icon size={15} style={{ display: "block", margin: "0 auto 3px" }} color="#0E7A4F" />
                  <span style={{ fontSize: 9.5, fontWeight: 700 }}>{b.l}</span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <button data-testid="mc-home-add" onClick={onEditHome} style={{ width: "100%", padding: "14px 10px", borderRadius: 11, background: "#F0FDF4", border: "1.5px dashed #0E7A4F", cursor: "pointer", color: "#0E7A4F", fontWeight: 800, fontSize: 12.5 }}>
            + Enregistrer mon domicile principal (adresse CIV)
          </button>
        )}
      </div>
      <div style={{ ...T.row2, marginTop: 8 }}>
        <button data-testid="mc-other-addresses" onClick={() => goTab("addresses")} style={T.srvRow}>
          <span style={T.srvIcon("#FEF3C7", "#B45309")}><Home size={16} /></span>
          <span style={{ minWidth: 0, flex: 1 }}><span style={T.srvT}>Mes autres adresses</span><br /><span style={T.srvS}>Bureau, Boutique, École…</span></span>
          <span style={{ minWidth: 21, height: 21, borderRadius: 999, background: "#0E7A4F", color: "#fff", fontSize: 10.5, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 5px" }}>{others?.length || 0}</span>
          <ChevronRight size={13} color="#9CA3AF" />
        </button>
        <button data-testid="mc-named-homes" onClick={() => goTab("addresses")} style={T.srvRow}>
          <span style={T.srvIcon("#FFEDD5", "#EA580C")}><Tag size={16} /></span>
          <span style={{ minWidth: 0, flex: 1 }}><span style={T.srvT}>Mes maisons nommées</span><br /><span style={T.srvS}>Maison, Boutique Aya…</span></span>
          <span style={{ minWidth: 21, height: 21, borderRadius: 999, background: "#EA580C", color: "#fff", fontSize: 10.5, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 5px" }}>{namedCount}</span>
          <ChevronRight size={13} color="#9CA3AF" />
        </button>
      </div>

      {/* ══ MES SERVICES AYA ══ */}
      <div style={T.secHead}>
        <span style={T.secIcon}><Star size={13} color="#0E7A4F" /></span>
        <h2 style={T.secTitle}>Mes services Aya</h2>
      </div>
      <div style={T.row2} data-testid="mc-services-grid">
        {SERVICES.map((s) => (
          <button key={s.t} data-testid={`mc-service-${s.t.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={s.act} style={T.srvRow}>
            <span style={T.srvIcon(s.bg, s.c)}><s.Icon size={16} /></span>
            <span style={{ minWidth: 0, flex: 1 }}><span style={T.srvT}>{s.t}</span><br /><span style={T.srvS}>{s.s}</span></span>
            <ChevronRight size={13} color="#9CA3AF" />
          </button>
        ))}
      </div>

      {/* ══ PARAMÈTRES DU COMPTE ══ */}
      <div style={T.secHead} id="mc-settings-grid">
        <span style={T.secIcon}><Settings size={13} color="#374151" /></span>
        <h2 style={T.secTitle}>Paramètres du compte</h2>
      </div>
      <div style={{ ...T.row2, marginBottom: 16 }} data-testid="mc-settings-grid-list">
        {SETTINGS.map((s) => (
          <button key={s.t} data-testid={`mc-setting-${s.t.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={s.act} style={T.srvRow}>
            <span style={T.srvIcon(s.bg, s.c)}><s.Icon size={16} /></span>
            <span style={{ minWidth: 0, flex: 1 }}><span style={T.srvT}>{s.t}</span><br /><span style={T.srvS}>{s.s}</span></span>
            <ChevronRight size={13} color="#9CA3AF" />
          </button>
        ))}
      </div>

      {/* ══ MODAL PARTAGE ADRESSE (carte visuelle AYA) ══ */}
      <AddressShareCard open={shareOpen} addr={home} onClose={() => setShareOpen(false)} />

      {/* ══ MODAL HISTORIQUE TRANSFERTS ══ */}
      {histOpen && (
        <div data-testid="mc-hist-modal" onClick={() => setHistOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(4,18,11,0.75)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 20, padding: "18px 16px", maxWidth: 360, width: "100%", maxHeight: "72vh", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <span style={{ fontSize: 15, fontWeight: 900, color: "#0E7A4F" }}>Historique des transferts</span>
              <button data-testid="mc-hist-close" onClick={() => setHistOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7280", display: "flex" }}><X size={18} /></button>
            </div>
            <div style={{ overflowY: "auto", flex: 1 }} data-testid="mc-hist-list">
              {transfers === null && <div style={{ fontSize: 12, color: "#6B7280", textAlign: "center", padding: 16 }}>⏳ Chargement…</div>}
              {transfers && transfers.length === 0 && <div style={{ fontSize: 12, color: "#6B7280", textAlign: "center", padding: 16 }}>Aucun transfert pour l&apos;instant.</div>}
              {(transfers || []).map((t) => (
                <div key={t.transferId} data-testid={`mc-hist-${t.transferId}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 4px", borderBottom: "1px solid #F3F4F6" }}>
                  <span style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: t.direction === "sent" ? "#FEE2E2" : "#DCFCE7" }}>
                    {t.direction === "sent" ? <ArrowUp size={15} color="#DC2626" /> : <ArrowDown size={15} color="#0E7A4F" />}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 12, fontWeight: 800, color: "#1F2937", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {t.direction === "sent" ? `Envoyé à ${t.to_name || t.to}` : `Reçu de ${t.from}`}
                    </span>
                    <span style={{ fontSize: 9.5, color: "#9CA3AF" }}>{(t.at || "").slice(0, 16).replace("T", " · ")}</span>
                  </span>
                  <span style={{ fontSize: 12.5, fontWeight: 900, color: t.direction === "sent" ? "#DC2626" : "#0E7A4F", whiteSpace: "nowrap" }}>
                    {t.direction === "sent" ? "−" : "+"}{fmt(t.amount_fcfa)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ══ MODAL ENVOI D'ARGENT ══ */}
      {sendOpen && (
        <div data-testid="mc-send-modal" onClick={() => !sending && setSendOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(4,18,11,0.75)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 20, padding: "20px 20px 18px", maxWidth: 330, width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <span style={{ fontSize: 15, fontWeight: 900, color: "#0E7A4F" }}>Envoyer de l&apos;argent</span>
              <button data-testid="mc-send-close" onClick={() => setSendOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7280", display: "flex" }}><X size={18} /></button>
            </div>
            <div style={{ fontSize: 10, fontWeight: 800, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Destinataire (numéro ou QR aya:user:…)</div>
            <input data-testid="mc-send-to" value={sendTo} onChange={(e) => setSendTo(e.target.value)} placeholder="+2250701234567 ou colle son QR"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1.5px solid #D1D5DB", fontSize: 13, boxSizing: "border-box", outline: "none", color: "#1F2937" }} />
            <div style={{ fontSize: 10, fontWeight: 800, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, margin: "10px 0 4px" }}>Montant (FCFA)</div>
            <input data-testid="mc-send-amount" type="number" min="100" step="100" value={sendAmount} onChange={(e) => setSendAmount(e.target.value)} placeholder="Ex : 5000"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1.5px solid #D1D5DB", fontSize: 13, boxSizing: "border-box", outline: "none", color: "#1F2937" }} />
            {balance !== null && <div style={{ fontSize: 10.5, color: "#6B7280", marginTop: 6 }}>Solde disponible : {fmt(balance)}</div>}
            <button data-testid="mc-send-confirm" disabled={sending} onClick={async () => {
              const amt = parseInt(sendAmount, 10);
              if (!sendTo.trim()) { toast.error("Indique le destinataire"); return; }
              if (!Number.isInteger(amt) || amt < 100) { toast.error("Montant minimum : 100 FCFA"); return; }
              setSending(true);
              try {
                const r = await fetch(`${API}/v3/wallet/transfer`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ to: sendTo.trim(), amount_fcfa: amt, idempotency_key: `trf_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` }),
                });
                const b = await r.json();
                if (!r.ok || !b.ok) {
                  const msg = { recipient_not_found: "Destinataire introuvable sur AYA", insufficient_funds: "Solde insuffisant", cannot_send_to_self: "Impossible de s'envoyer à soi-même", invalid_recipient: "Numéro invalide" }[b.error] || "Transfert impossible";
                  toast.error(msg);
                } else {
                  setBalance(b.new_balance_fcfa);
                  setSendOpen(false); setSendTo(""); setSendAmount("");
                  toast.success(`✅ ${fmt(amt)} envoyés à ${b.transfer.to_name || b.transfer.to}`);
                }
              } catch { toast.error("Erreur réseau"); }
              finally { setSending(false); }
            }} style={{ marginTop: 14, width: "100%", padding: "11px", borderRadius: 11, background: "linear-gradient(135deg,#10B981,#0E7A4F)", color: "#fff", border: "none", fontWeight: 800, fontSize: 13, cursor: "pointer", opacity: sending ? 0.6 : 1 }}>
              {sending ? "Envoi en cours…" : "Envoyer maintenant"}
            </button>
            <div style={{ fontSize: 9, color: "#9CA3AF", marginTop: 7, textAlign: "center" }}>Transfert instantané entre wallets AYA (sandbox) — plafond 500 000 FCFA</div>
          </div>
        </div>
      )}

      {/* ══ MODAL QR ══ */}
      {qrOpen && (
        <div data-testid="mc-qr-modal" onClick={() => setQrOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(4,18,11,0.75)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 20, padding: "22px 22px 18px", textAlign: "center", maxWidth: 300, width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <span style={{ fontSize: 15, fontWeight: 900, color: "#0E7A4F" }}>Mon QR AYA</span>
              <button data-testid="mc-qr-close" onClick={() => setQrOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7280", display: "flex" }}><X size={18} /></button>
            </div>
            <QRCodeSVG value={`aya:user:${user?.phone || "anonyme"}`} size={180} level="M" fgColor="#06281A" data-testid="mc-qr-svg" />
            <div style={{ fontSize: 12.5, fontWeight: 800, color: "#1F2937", marginTop: 12 }}>{displayName}</div>
            <div style={{ fontSize: 10.5, color: "#6B7280", marginTop: 2 }}>Scanne pour retrouver mon profil AYA</div>
            <button data-testid="mc-qr-copy" onClick={() => { navigator.clipboard?.writeText(`aya:user:${user?.phone || ""}`); toast.success("Identifiant AYA copié ✓"); }} style={{ marginTop: 12, width: "100%", padding: "10px", borderRadius: 11, background: "linear-gradient(135deg,#10B981,#0E7A4F)", color: "#fff", border: "none", fontWeight: 800, fontSize: 12.5, cursor: "pointer" }}>
              Copier mon identifiant
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
